import React, {useCallback, useMemo, useState} from "react";
import {
    Box,
    Button,
    Checkbox,
    Container,
    Grid,
    Menu,
    MenuItem,
    TextField,
    Typography
} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import {
    DataGridPro,
    GridFooterContainer,
    GridColDef,
    GridColumnOrderChangeParams,
    GridColumnResizeParams,
    GridSortModel,
    GridColumnVisibilityChangeParams,
    useGridApiRef,
    useGridApiContext,
    useGridApiEventHandler,
} from "@mui/x-data-grid-pro";
import {endOfMonth, format} from "date-fns";
import Page from "../../components/Page";
import {DataGridLocale} from "../../common/DataGridLocale";
import {formatCurrency, HasRole, saveFilePost} from "../../common/Util";
import {CustomerTypeMap, ProductOriginMap} from "../../common/Constant";
import {
    RoleIdEnum,
    useGetCustomerBriefListQuery,
    useGetOrdersQuery,
    useGetPerformanceTableQuery,
    useGetUsersQuery
} from "../../generated/graphql";
import {
    actualTextStyle,
    forecastTextStyle,
    getMetricTextStyle,
    parseViewMode,
    PerformanceViewMode,
} from "./performanceTableDisplay";

type MonthMetric = {
    month: string;
    revenue: number;
    cost: number;
    profit: number;
    kind: string;
    actualRevenue?: number;
    actualProfit?: number;
    forecastRevenue?: number;
    forecastProfit?: number;
};

const viewModeOptions: Array<{ value: PerformanceViewMode; label: string }> = [
    { value: "COMBINED", label: "實際 + Forecast" },
    { value: "ACTUAL", label: "僅實際" },
    { value: "FORECAST", label: "僅 Forecast" },
];

const getDisplayAmounts = (metric: MonthMetric, viewMode: PerformanceViewMode) => {
    if (viewMode === "ACTUAL") {
        return { revenue: metric.actualRevenue || 0, profit: metric.actualProfit || 0 };
    }
    if (viewMode === "FORECAST") {
        return { revenue: metric.forecastRevenue || 0, profit: metric.forecastProfit || 0 };
    }
    return { revenue: metric.revenue || 0, profit: metric.profit || 0 };
};

const gridId = "performanceTable";
const columnsOrderKey = `columnsOrder_${gridId}`;
const columnWidthKeyPrefix = `columnWidth_${gridId}_`;
const columnsVisibilityKey = `columnsVisibility_${gridId}`;
const filtersKey = `${gridId}_filters`;

const toMonthFirstDay = (month: string): string => {
    const [year, mon] = month.split("-");
    return new Date(Number(year), Number(mon) - 1, 1).toISOString();
};

const toMonthLastDay = (month: string): string => {
    const [year, mon] = month.split("-");
    return endOfMonth(new Date(Number(year), Number(mon) - 1, 1)).toISOString();
};

const toMonthFirstDayString = (month: string): string => {
    const [year, mon] = month.split("-");
    return format(new Date(Number(year), Number(mon) - 1, 1), "yyyy-MM-dd");
};

const toMonthLastDayString = (month: string): string => {
    const [year, mon] = month.split("-");
    return format(endOfMonth(new Date(Number(year), Number(mon) - 1, 1)), "yyyy-MM-dd");
};

const normalizeMonth = (month: string) => {
    if (!month) {
        return "";
    }
    const digits = month.replace(/[^0-9]/g, "");
    if (digits.length >= 6) {
        return digits.slice(0, 6);
    }
    return month;
};

const getProductOriginLabel = (value?: string | null) => {
    if (!value) {
        return "";
    }
    const direct = ProductOriginMap[value];
    if (direct) {
        return direct;
    }
    const matchedKey = Object.keys(ProductOriginMap).find(
        (key) => key.toLowerCase() === value.toLowerCase()
    );
    if (matchedKey) {
        return ProductOriginMap[matchedKey];
    }
    return value;
};

const formatMonthLabel = (month: string) => {
    const normalized = normalizeMonth(month);
    if (normalized.length === 6) {
        const year = Number(normalized.slice(0, 4));
        const mon = Number(normalized.slice(4, 6));
        return format(new Date(year, mon - 1, 1), "yyyy/MM");
    }
    return month;
};

const getMonthsInRange = (startMonth: string, endMonth: string) => {
    const [startYear, startMon] = startMonth.split("-").map(Number);
    const [endYear, endMon] = endMonth.split("-").map(Number);
    const months: string[] = [];
    let current = new Date(startYear, startMon - 1, 1);
    const end = new Date(endYear, endMon - 1, 1);
    while (current <= end) {
        months.push(format(current, "yyyyMM"));
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    }
    return months;
};

const totalAwareComparator = (v1: any, v2: any, params1?: any, params2?: any) => {
    const isTotal1 = Boolean(params1?.row?.isTotal);
    const isTotal2 = Boolean(params2?.row?.isTotal);
    if (isTotal1 && isTotal2) return 0;
    if (isTotal1) return 1;
    if (isTotal2) return -1;
    if (v1 == null && v2 == null) return 0;
    if (v1 == null) return 1;
    if (v2 == null) return -1;
    const n1 = typeof v1 === "number" ? v1 : Number(v1);
    const n2 = typeof v2 === "number" ? v2 : Number(v2);
    if (!Number.isNaN(n1) && !Number.isNaN(n2)) {
        return n1 - n2;
    }
    return String(v1).localeCompare(String(v2), "zh-Hant");
};


export default () => {
    const now = new Date();
    const defaultStartMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const endMonthDate = new Date(now.getFullYear(), now.getMonth() + 11, 1);
    const defaultEndMonth = `${endMonthDate.getFullYear()}-${String(endMonthDate.getMonth() + 1).padStart(2, "0")}`;

    const [filters, setFilters] = useState(() => {
        const stored = localStorage.getItem(filtersKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                const startMonth = typeof parsed.startMonth === "string" ? parsed.startMonth : defaultStartMonth;
                const endMonth = typeof parsed.endMonth === "string" ? parsed.endMonth : defaultEndMonth;
                return {
                    startMonth,
                    endMonth,
                    customerIds: Array.isArray(parsed.customerIds) ? parsed.customerIds : [],
                    orderIds: Array.isArray(parsed.orderIds) ? parsed.orderIds : [],
                    assignedSalesUserIds: Array.isArray(parsed.assignedSalesUserIds) ? parsed.assignedSalesUserIds : [],
                    assigneeIds: Array.isArray(parsed.assigneeIds) ? parsed.assigneeIds : [],
                    viewMode: parseViewMode(parsed.viewMode),
                };
            } catch (e) {
                console.warn("Failed to parse stored performance filters", e);
            }
        }
        return {
            startMonth: defaultStartMonth,
            endMonth: defaultEndMonth,
            customerIds: [] as string[],
            orderIds: [] as string[],
            assignedSalesUserIds: [] as string[],
            assigneeIds: [] as string[],
            viewMode: "COMBINED" as PerformanceViewMode,
        };
    });

    const [columnOrder, setColumnOrder] = useState<string[] | null>(() => {
        const stored = localStorage.getItem(columnsOrderKey);
        return stored ? JSON.parse(stored) : null;
    });
    const apiRef = useGridApiRef();
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
        const stored = localStorage.getItem(columnsVisibilityKey);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.warn("Failed to parse stored column visibility", e);
            }
        }
        return {
            orderItemId: true,
            orderNumber: true,
            orderName: true,
            customerName: true,
            type: true,
            assignedSalesUserName: true,
            assigneeName: true,
        };
    });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const performanceFilter = useMemo(() => ({
        startDate: toMonthFirstDay(filters.startMonth),
        endDate: toMonthLastDay(filters.endMonth),
        orderIds: filters.orderIds.length ? filters.orderIds : undefined,
        customerIds: filters.customerIds.length ? filters.customerIds : undefined,
        assignedSalesUserIds: filters.assignedSalesUserIds.length ? filters.assignedSalesUserIds : undefined,
        assigneeIds: filters.assigneeIds.length ? filters.assigneeIds : undefined,
    }), [filters]);

    const {data, loading} = useGetPerformanceTableQuery({
        variables: {
            filter: performanceFilter,
        },
    });
    const {data: customersData} = useGetCustomerBriefListQuery();
    const {data: ordersData} = useGetOrdersQuery({
        variables: {
            pagination: {pageIndex: 0, pageSize: 3000},
            filtering: [],
            ordering: {id: "id", desc: true},
        },
    });
    const {data: usersData} = useGetUsersQuery();

    const months = useMemo(() => {
        if (data?.performanceTable?.months?.length) {
            return data.performanceTable.months;
        }
        return getMonthsInRange(filters.startMonth, filters.endMonth);
    }, [data, filters.startMonth, filters.endMonth]);

    const rows = useMemo(() => {
        return (data?.performanceTable?.rows || []).map((row) => {
            const metricsByMonth = row.metrics.reduce((acc: Record<string, MonthMetric>, metric) => {
                acc[normalizeMonth(metric.month)] = metric as MonthMetric;
                return acc;
            }, {});
            return {
                id: row.orderItemId,
                ...row,
                metricsByMonth,
                isTotal: (row as any).isTotal,
            };
        }).filter((row) => !row.isTotal);
    }, [data]);


    const dimensionColumns: GridColDef[] = useMemo(() => ([
        {
            field: "orderItemId",
            headerName: "委刊細項 ID",
            width: 140,
            sortable: true,
            sortComparator: totalAwareComparator,
            renderCell: (params) => (
                params.row.isTotal ? "" : (
                    <a href={`/app/orderItem/${params.row.orderItemId}`} target="_blank" rel="noreferrer">
                        {params.row.orderItemId}
                    </a>
                )
            ),
        },
        {
            field: "orderNumber",
            headerName: "委刊單號",
            width: 150,
            sortComparator: totalAwareComparator,
            renderCell: (params) => (
                params.row.isTotal ? "" : (
                    <a href={`/app/order/${params.row.orderId}`} target="_blank" rel="noreferrer">
                        {params.value}
                    </a>
                )
            ),
        },
        {
            field: "orderName",
            headerName: "專案名稱",
            width: 260,
            sortComparator: totalAwareComparator,
            valueGetter: (params) => (params.row.isTotal ? "" : params.row.orderName),
        },
        {field: "productName", headerName: "產品名稱", width: 160, sortComparator: totalAwareComparator},
        {
            field: "productOrigin",
            headerName: "原廠商",
            width: 140,
            valueGetter: (params) => getProductOriginLabel(params.row.productOrigin as string),
            sortComparator: totalAwareComparator,
        },
        {field: "customerName", headerName: "客戶名稱", width: 160, sortComparator: totalAwareComparator},
        {field: "advertiserName", headerName: "廣告主", width: 160, sortComparator: totalAwareComparator},
        {
            field: "customerType",
            headerName: "客戶類型",
            width: 120,
            valueGetter: (params) => CustomerTypeMap[params.row.customerType as string] || params.row.customerType,
            sortComparator: totalAwareComparator,
        },
        {
            field: "type",
            headerName: "操作類型",
            width: 140,
            valueGetter: (params) => params.row.typeLabel || params.row.type,
            sortComparator: totalAwareComparator,
        },
        {field: "assignedSalesUserName", headerName: "簽約負責人", width: 140, sortComparator: totalAwareComparator},
        {field: "assigneeName", headerName: "專案負責人", width: 140, sortComparator: totalAwareComparator},
        {field: "supporterName", headerName: "專案執行人", width: 140, sortComparator: totalAwareComparator},
        {
            field: "cooperationYear",
            headerName: "合作年數",
            width: 120,
            sortComparator: totalAwareComparator,
            valueGetter: (params) => {
                if (params.row.isTotal) {
                    return "";
                }
                return params.row.cooperationYear || "";
            },
        },
        {
            field: "startDate",
            headerName: "走期_起",
            width: 140,
            valueGetter: (params) => (params.row.startDate ? format(new Date(params.row.startDate), "yyyy-MM-dd") : ""),
            sortComparator: totalAwareComparator,
        },
        {
            field: "endDate",
            headerName: "走期_迄",
            width: 140,
            valueGetter: (params) => (params.row.endDate ? format(new Date(params.row.endDate), "yyyy-MM-dd") : ""),
            sortComparator: totalAwareComparator,
        },
    ]), []);

    const metricColumns: GridColDef[] = useMemo(() => (
        months.map((month) => {
            const normalized = normalizeMonth(month);
            const field = `m_${normalized}`;
            return {
                field,
                headerName: formatMonthLabel(month),
                width: 140,
                sortable: false,
                filterable: false,
                sortComparator: totalAwareComparator,
                renderCell: (params) => {
                    const metric = params.row.metricsByMonth?.[normalized] as MonthMetric | undefined;
                    if (!metric) {
                        return <span style={{color: "#9e9e9e"}}>-</span>;
                    }
                    const textStyle = getMetricTextStyle(metric.kind, filters.viewMode);
                    const amounts = getDisplayAmounts(metric, filters.viewMode);
                    return (
                        <div style={{display: "flex", flexDirection: "column", lineHeight: 1.2}}>
                            <span style={textStyle}>
                                {formatCurrency(amounts.revenue || 0)}
                            </span>
                            <span style={textStyle}>
                                {formatCurrency(amounts.profit || 0)}
                            </span>
                        </div>
                    );
                },
            } as GridColDef;
        })
    ), [months, filters.viewMode]);

    const columns = useMemo(() => {
        const baseColumns = [...dimensionColumns, ...metricColumns].map((col) => {
            if (typeof columnVisibilityModel[col.field] === "boolean") {
                return {
                    ...col,
                    hide: columnVisibilityModel[col.field] === false,
                };
            }
            return col;
        });
        if (!columnOrder || columnOrder.length === 0) {
            return baseColumns;
        }
        const columnMap = new Map(baseColumns.map((col) => [col.field, col]));
        const ordered = columnOrder
            .map((field) => columnMap.get(field))
            .filter(Boolean) as GridColDef[];
        const missing = baseColumns.filter((col) => !columnOrder.includes(col.field));
        return [...ordered, ...missing];
    }, [dimensionColumns, metricColumns, columnOrder, columnVisibilityModel]);

    const rowsSortedForExport = useMemo(() => {
        if (!sortModel || sortModel.length === 0) {
            return rows;
        }
        const { field, sort } = sortModel[0];
        if (!field || !sort) {
            return rows;
        }
        const colDef = columns.find((col) => col.field === field);
        const getValue = (row: any) => {
            if (colDef?.valueGetter) {
                return colDef.valueGetter({ row, field, value: row[field] } as any);
            }
            return row[field];
        };
        const sorted = [...rows].sort((a, b) => {
            const aVal = getValue(a);
            const bVal = getValue(b);
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const aNum = typeof aVal === "number" ? aVal : Number(aVal);
            const bNum = typeof bVal === "number" ? bVal : Number(bVal);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
                return aNum - bNum;
            }
            const aStr = String(aVal);
            const bStr = String(bVal);
            return aStr.localeCompare(bStr, "zh-Hant");
        });
        return sort === "desc" ? sorted.reverse() : sorted;
    }, [columns, rows, sortModel]);

    const handleColumnOrderChange = useCallback((params: GridColumnOrderChangeParams) => {
        const newColumns = [...columns];
        const [moved] = newColumns.splice(params.oldIndex, 1);
        newColumns.splice(params.targetIndex, 0, moved);
        const newOrder = newColumns.map((column) => column.field);
        setColumnOrder(newOrder);
        localStorage.setItem(columnsOrderKey, JSON.stringify(newOrder));
    }, [columns]);

    const handleColumnVisibilityChange = useCallback((field: string) => {
        setColumnVisibilityModel((prev) => {
            const currentVisible = prev[field] !== false;
            const next = {
                ...prev,
                [field]: !currentVisible,
            };
            localStorage.setItem(columnsVisibilityKey, JSON.stringify(next));
            return next;
        });
    }, []);

    const handleGridColumnVisibilityChange = useCallback((params: GridColumnVisibilityChangeParams) => {
        setColumnVisibilityModel((prev) => {
            const next = {
                ...prev,
                [params.field]: params.isVisible,
            };
            localStorage.setItem(columnsVisibilityKey, JSON.stringify(next));
            return next;
        });
    }, []);


    const handleFilterChange = useCallback((key: string, value: any) => {
        setFilters((prev) => {
            let next = {...prev, [key]: value};
            if (key === "startMonth") {
                const start = new Date(value);
                const end = new Date(prev.endMonth);
                if (start > end) {
                    const adjusted = new Date(start);
                    adjusted.setMonth(start.getMonth() + 6);
                    next.endMonth = `${adjusted.getFullYear()}-${String(adjusted.getMonth() + 1).padStart(2, "0")}`;
                }
            }
            if (key === "endMonth") {
                const start = new Date(prev.startMonth);
                const end = new Date(value);
                if (end < start) {
                    const adjusted = new Date(end);
                    adjusted.setMonth(end.getMonth() - 6);
                    next.startMonth = `${adjusted.getFullYear()}-${String(adjusted.getMonth() + 1).padStart(2, "0")}`;
                }
            }
            return next;
        });
    }, []);

    React.useEffect(() => {
        localStorage.setItem(filtersKey, JSON.stringify(filters));
    }, [filters]);

    const downloadExcel = useCallback(async () => {
        const exportColumns: Array<{field: string}> = [];
        const exportVisibleColumns: string[] = [];
        columns.forEach((column) => {
            const field = column.field as string;
            const isVisible = columnVisibilityModel[field] !== false && column.hide !== true;
            if (field.startsWith("m_")) {
                const monthKey = field.replace("m_", "");
                const monthFields = [
                    `${monthKey}收入`,
                    `${monthKey}成本`,
                    `${monthKey}毛利`,
                ];
                monthFields.forEach((monthField) => {
                    exportColumns.push({field: monthField});
                    if (isVisible) {
                        exportVisibleColumns.push(monthField);
                    }
                });
                return;
            }
            exportColumns.push({field});
            if (isVisible) {
                exportVisibleColumns.push(field);
            }
        });

        const sortedRowIds = apiRef.current?.getSortedRowIds?.();
        const rowModels = apiRef.current?.getRowModels?.();
        const visibleLookup = apiRef.current?.state?.filter?.visibleRowsLookup;
        let exportRows = rowsSortedForExport;
        if (sortedRowIds && rowModels) {
            exportRows = sortedRowIds
                .filter((id) => (visibleLookup ? visibleLookup[id] !== false : true))
                .map((id) => rowModels.get(id))
                .filter(Boolean) as any[];
        }
        const orderItemIds = exportRows
            .filter((row: any) => !row.isTotal && row.orderItemId && row.orderItemId !== 0)
            .map((row: any) => row.orderItemId || row.id)
            .filter(Boolean);

        await saveFilePost(
            `/reports/performance-table`,
            `performance-table-${format(new Date(), "yyyy-MM-dd")}.xlsx`,
            {
                startDate: toMonthFirstDayString(filters.startMonth),
                endDate: toMonthLastDayString(filters.endMonth),
                columns: exportColumns,
                visibleColumns: exportVisibleColumns,
                orderItemIds,
                viewMode: filters.viewMode,
            }
        );
    }, [apiRef, columns, columnVisibilityModel, filters, rowsSortedForExport]);

    const customerOptions = useMemo(() => customersData?.result?.list || [], [customersData]);
    const orderOptions = useMemo(() => ordersData?.result?.list || [], [ordersData]);
    const userOptions = useMemo(() => usersData?.users || [], [usersData]);

    const columnsMenuOpen = Boolean(columnMenuAnchor);

    const CustomFooter = () => {
        const apiRef = useGridApiContext();
        const [scrollLeft, setScrollLeft] = useState(0);
        useGridApiEventHandler(apiRef, "rowsScroll", (params) => {
            setScrollLeft(params.left);
        });
        React.useEffect(() => {
            const left = apiRef.current.getScrollPosition?.().left ?? 0;
            setScrollLeft(left);
        }, [apiRef]);
        const visibleColumns = apiRef.current.getVisibleColumns();
        const rowModels = apiRef.current.getVisibleRowModels();
        const rows = Array.from(rowModels.values()) as any[];
        const actualTotalsByMonth = rows.reduce<Record<string, { revenue: number; profit: number }>>((acc, row) => {
            Object.entries(row.metricsByMonth || {}).forEach(([monthKey, metric]) => {
                const metricTyped = metric as MonthMetric;
                if (!acc[monthKey]) {
                    acc[monthKey] = { revenue: 0, profit: 0 };
                }
                if (filters.viewMode !== "FORECAST") {
                    acc[monthKey].revenue += metricTyped.actualRevenue || 0;
                    acc[monthKey].profit += metricTyped.actualProfit || 0;
                }
            });
            return acc;
        }, {});
        const forecastTotalsByMonth = rows.reduce<Record<string, { revenue: number; profit: number }>>((acc, row) => {
            Object.entries(row.metricsByMonth || {}).forEach(([monthKey, metric]) => {
                const metricTyped = metric as MonthMetric;
                if (!acc[monthKey]) {
                    acc[monthKey] = { revenue: 0, profit: 0 };
                }
                if (filters.viewMode !== "ACTUAL") {
                    acc[monthKey].revenue += metricTyped.forecastRevenue || 0;
                    acc[monthKey].profit += metricTyped.forecastProfit || 0;
                }
            });
            return acc;
        }, {});
        const columnsTotalWidth = visibleColumns.reduce((sum, col) => {
            const width = (col as any).computedWidth ?? col.width ?? 140;
            return sum + width;
        }, 0);
        const renderTotalRow = (label: string, totalsByMonth: Record<string, { revenue: number; profit: number }>, textStyle: typeof actualTextStyle | typeof forecastTextStyle) => (
            <div
                style={{
                    display: "flex",
                    width: columnsTotalWidth || "100%",
                    minWidth: columnsTotalWidth || "100%",
                    transform: `translateX(${-scrollLeft}px)`,
                }}
            >
                {visibleColumns.map((col, index) => {
                    const field = col.field as string;
                    const width = (col as any).computedWidth ?? col.width ?? 140;
                    if (!field.startsWith("m_")) {
                        return (
                            <div
                                key={field}
                                style={{
                                    width,
                                    minWidth: width,
                                    padding: "8px 8px",
                                    boxSizing: "border-box",
                                    borderTop: "1px solid #e0e0e0",
                                }}
                            >
                                {index === 0 ? <span style={textStyle}>{label}</span> : null}
                            </div>
                        );
                    }
                    const monthKey = field.replace("m_", "");
                    const total = totalsByMonth[monthKey] || { revenue: 0, profit: 0 };
                    return (
                        <div
                            key={field}
                            style={{
                                width,
                                minWidth: width,
                                padding: "8px 8px",
                                boxSizing: "border-box",
                                borderTop: "1px solid #e0e0e0",
                            }}
                        >
                            <div style={{display: "flex", flexDirection: "column", lineHeight: 1.2}}>
                                <span style={textStyle}>{formatCurrency(total.revenue || 0)}</span>
                                <span style={textStyle}>{formatCurrency(total.profit || 0)}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
        return (
            <GridFooterContainer style={{ overflow: "hidden", flexDirection: "column", alignItems: "flex-start" }}>
                {renderTotalRow("實際總計", actualTotalsByMonth, actualTextStyle)}
                {renderTotalRow("Forecast 總計", forecastTotalsByMonth, forecastTextStyle)}
            </GridFooterContainer>
        );
    };

    return (
        <HasRole roles={[RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.PmManager, RoleIdEnum.Gm, RoleIdEnum.Finance, RoleIdEnum.Secretary, RoleIdEnum.Marketing, RoleIdEnum.Coo]}>
            <Page title="Performance Table">
                <Container maxWidth={false} style={{height: "100vh", display: "flex", flexDirection: "column", padding: "30px"}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="開始月份"
                                type="month"
                                value={filters.startMonth}
                                onChange={(e) => handleFilterChange("startMonth", e.target.value)}
                                variant="outlined"
                                InputLabelProps={{shrink: true}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="結束月份"
                                type="month"
                                value={filters.endMonth}
                                onChange={(e) => handleFilterChange("endMonth", e.target.value)}
                                variant="outlined"
                                InputLabelProps={{shrink: true}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                multiple
                                options={customerOptions}
                                getOptionLabel={(option: any) => option?.name || ""}
                                value={customerOptions.filter((c) => filters.customerIds.includes(c.id))}
                                onChange={(event, value) =>
                                    handleFilterChange("customerIds", value.map((item: any) => item.id))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="客戶" placeholder="選擇客戶" />
                                )}
                                filterSelectedOptions
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                multiple
                                options={orderOptions}
                                getOptionLabel={(option: any) => option?.name || option?.number || ""}
                                value={orderOptions.filter((o) => filters.orderIds.includes(o.id))}
                                onChange={(event, value) =>
                                    handleFilterChange("orderIds", value.map((item: any) => item.id))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="專案" placeholder="選擇專案" />
                                )}
                                filterSelectedOptions
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                multiple
                                options={userOptions}
                                getOptionLabel={(option: any) => option?.name || ""}
                                value={userOptions.filter((u) => filters.assignedSalesUserIds.includes(u.id))}
                                onChange={(event, value) =>
                                    handleFilterChange("assignedSalesUserIds", value.map((item: any) => item.id))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="簽約負責人" placeholder="選擇人員" />
                                )}
                                filterSelectedOptions
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                multiple
                                options={userOptions}
                                getOptionLabel={(option: any) => option?.name || ""}
                                value={userOptions.filter((u) => filters.assigneeIds.includes(u.id))}
                                onChange={(event, value) =>
                                    handleFilterChange("assigneeIds", value.map((item: any) => item.id))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="專案負責人" placeholder="選擇人員" />
                                )}
                                filterSelectedOptions
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="實際 / Forecast"
                                select
                                value={filters.viewMode}
                                onChange={(e) => handleFilterChange("viewMode", parseViewMode(e.target.value))}
                                variant="outlined"
                                InputLabelProps={{shrink: true}}
                                fullWidth
                            >
                                {viewModeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box display="flex" alignItems="center" height="100%">
                                {rows.length > 0 && (
                                    <Button color="primary" variant="contained" onClick={downloadExcel}>
                                        下載 Excel
                                    </Button>
                                )}
                                <Button
                                    style={{marginLeft: 12}}
                                    variant="outlined"
                                    onClick={(event) => setColumnMenuAnchor(event.currentTarget)}
                                >
                                    欄位設定
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box style={{marginTop: 20, flex: 1, minHeight: 0}}>
                        {loading ? (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                <Typography variant="body1">資料載入中...</Typography>
                            </Box>
                        ) : (
                            <DataGridPro
                                apiRef={apiRef}
                                rows={rows}
                                columns={columns.map((column) => ({
                                    width: parseInt(localStorage.getItem(`${columnWidthKeyPrefix}${column.field}`) ?? "140"),
                                    ...column,
                                }))}
                                loading={loading}
                                disableColumnMenu={false}
                                disableColumnSelector={false}
                                onColumnVisibilityChange={handleGridColumnVisibilityChange}
                                sortModel={sortModel}
                                onSortModelChange={(model) => setSortModel(model)}
                                onColumnOrderChange={handleColumnOrderChange}
                                onColumnWidthChange={(param: GridColumnResizeParams) => {
                                    localStorage.setItem(`${columnWidthKeyPrefix}${param.colDef.field}`, param.width.toString());
                                }}
                                disableSelectionOnClick
                                rowHeight={52}
                                headerHeight={52}
                                localeText={DataGridLocale}
                                components={{
                                    Footer: CustomFooter,
                                }}
                            />
                        )}
                    </Box>
                </Container>
                <Menu
                    anchorEl={columnMenuAnchor}
                    keepMounted
                    open={columnsMenuOpen}
                    onClose={() => setColumnMenuAnchor(null)}
                >
                    {columns.map((column) => {
                        const field = column.field as string;
                        const isVisible = columnVisibilityModel[field] !== false && column.hide !== true;
                        return (
                            <MenuItem key={field} onClick={() => handleColumnVisibilityChange(field)}>
                                <Checkbox checked={isVisible} color="primary" />
                                {column.headerName || field}
                            </MenuItem>
                        );
                    })}
                </Menu>
            </Page>
        </HasRole>
    );
};
